import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Copy, MessageCircle, Share } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { APP_URL } from "@/lib/constants";
import { useFriends } from "@/hooks/useFriends";
import { useTranslation } from "react-i18next";

export const InviteTab = () => {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralStats, setReferralStats] = useState({ count: 0 });
  const { user } = useAuth();
  const { refetch: refetchFriends } = useFriends();
  const { t } = useTranslation('friends');

  useEffect(() => {
    const fetchReferralData = async () => {
      if (!user?.id) return;

      try {
        // Get user's referral code
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('referral_code')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          return;
        }

        if (profile?.referral_code) {
          setReferralCode(profile.referral_code);
        }

        // Get referral stats
        const { data: referrals, error: referralsError } = await supabase
          .from('referrals')
          .select('id')
          .eq('referrer_id', user.id);

        if (referralsError) {
          console.error('Error fetching referrals:', referralsError);
          return;
        }

        setReferralStats({ count: referrals?.length || 0 });
      } catch (error) {
        console.error('Error fetching referral data:', error);
      }
    };

    fetchReferralData();
  }, [user?.id]);

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inviteEmail.trim()) {
      toast.error(t('invite.messages.emailRequired'));
      return;
    }

    if (!user?.email) {
      toast.error(t('invite.messages.loginRequired'));
      return;
    }

    setIsInviting(true);

    try {
      const userFirstName = user.user_metadata?.firstName || user.user_metadata?.first_name || "Ein Freund";
      
      const { data, error } = await supabase.functions.invoke('send-friend-invite', {
        body: {
          inviterName: userFirstName,
          inviterEmail: user.email,
          inviteeEmail: inviteEmail.trim(),
          inviteMessage: inviteMessage.trim() || undefined,
          referralCode: referralCode || undefined,
        },
      });

      if (error) {
        console.error('Error sending invite:', error);
        toast.error(t('invite.messages.sendError'));
        throw error;
      }

      console.log('Invite response:', data);

      if (data?.friendRequestCreated) {
        toast.success(t('invite.messages.sentWithRequest'));
      } else if (data?.userExists) {
        toast.success(t('invite.messages.sentToExisting'));
      } else {
        toast.success(t('invite.messages.sentSuccess'));
      }

      setInviteEmail("");
      setInviteMessage("");
      
      // Refresh friends data to show updated counts
      await refetchFriends();
      
    } catch (error) {
      console.error('Failed to send invite:', error);
      toast.error(t('invite.messages.sendError'));
    } finally {
      setIsInviting(false);
    }
  };

  const inviteLink = referralCode ? `${APP_URL}/invite?ref=${referralCode}` : "";

  const copyInviteLink = async () => {
    if (!inviteLink) {
      toast.error(t('invite.shareLink.linkUnavailable'));
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success(t('invite.messages.linkCopied'));
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error(t('invite.messages.copyError'));
    }
  };

  const shareViaWhatsApp = () => {
    if (!inviteLink) {
      toast.error(t('invite.shareLink.linkUnavailable'));
      return;
    }

    const message = t('invite.whatsappMessage', { link: inviteLink });
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareViaTelegram = () => {
    if (!inviteLink) {
      toast.error(t('invite.shareLink.linkUnavailable'));
      return;
    }

    const message = t('invite.telegramMessage', { link: inviteLink });
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(inviteLink)}&text=${encodeURIComponent(message)}`;
    window.open(telegramUrl, '_blank');
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">{t('invite.shareLink.title')}</h3>
        <Card className="uplivion-card">
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>{t('invite.shareLink.linkLabel')}</Label>
              <div className="flex gap-2">
                <Input
                  value={inviteLink}
                  readOnly
                  className="bg-gray-50"
                  placeholder={t('invite.shareLink.linkPlaceholder')}
                />
                <Button
                  onClick={copyInviteLink}
                  variant="outline"
                  size="icon"
                  disabled={!inviteLink}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={shareViaWhatsApp}
                variant="outline"
                className="gap-2"
                disabled={!inviteLink}
              >
                <MessageCircle className="h-4 w-4" />
                {t('invite.shareLink.actions.whatsapp')}
              </Button>
              
              <Button
                onClick={shareViaTelegram}
                variant="outline"
                className="gap-2"
                disabled={!inviteLink}
              >
                <Share className="h-4 w-4" />
                {t('invite.shareLink.actions.telegram')}
              </Button>
            </div>
            
            {referralStats.count > 0 && (
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600">
                  {t('invite.shareLink.stats', { count: referralStats.count })}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">{t('invite.email.title')}</h3>
        <Card className="uplivion-card">
          <CardContent className="p-6">
            <form onSubmit={handleSendInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="inviteEmail">{t('invite.email.emailLabel')}</Label>
                <Input
                  id="inviteEmail"
                  type="email"
                  placeholder={t('invite.email.emailPlaceholder')}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  disabled={isInviting}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="inviteMessage">{t('invite.email.messageLabel')}</Label>
                <Textarea
                  id="inviteMessage"
                  placeholder={t('invite.email.messagePlaceholder')}
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  rows={3}
                  disabled={isInviting}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full gap-2" 
                disabled={isInviting}
              >
                {isInviting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('invite.email.sendingButton')}
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4" />
                    {t('invite.email.sendButton')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
